// Device session — the browser half of the device passkey gate.
//
// The API (Reenbit.FMS.API/src/auth/deviceGate.js) expects an `X-FMS-Device`
// header carrying a short-lived HS256 JWT it minted after a WebAuthn assertion.
// This module keeps that token in localStorage['fms.deviceSession'], knows how
// to obtain one (status → assert options → passkey → assert verify), and hosts
// the tiny publish/subscribe channel the axios interceptor uses to hand a
// gate 403 code to <DeviceGate> without importing React.
//
// Nothing here throws on storage failures: private mode, quota, a blocked
// accessor — every read/write is wrapped, and "no session" is the fallback.

import axios from 'axios'
import { startAuthentication, startRegistration, browserSupportsWebAuthn } from '@simplewebauthn/browser'

export const STORAGE_KEY = 'fms.deviceSession'

// The exact codes the API puts in a 403 body (deviceGate.js CODES). A 403
// carrying one of these is an account/device state, not an error to toast.
export const GATE_CODES = Object.freeze({
  ACCESS_NOT_GRANTED: 'ACCESS_NOT_GRANTED',
  DEVICE_REQUIRED: 'DEVICE_REQUIRED',
  DEVICE_PENDING: 'DEVICE_PENDING',
  DEVICE_REVOKED: 'DEVICE_REVOKED',
  DEVICE_MISMATCH: 'DEVICE_MISMATCH'
})

// POST /v1/device/assert/options answers 404 with this when the user has no
// approved device — it is not a DEVICE_* 403.
export const NO_ACTIVE_DEVICE = 'NO_ACTIVE_DEVICE'

export const isGateCode = code => typeof code === 'string' && Object.prototype.hasOwnProperty.call(GATE_CODES, code)

// What ensureDeviceSession() resolves to. `state` is what the gate renders.
export const ENSURE_STATE = Object.freeze({
  CHOOSE: 'choose', // account has an approved passkey, but this browser has never used one here
  READY: 'ready',
  NOT_GRANTED: 'not-granted',
  ENROL: 'enrol',
  PENDING: 'pending',
  REVOKED: 'revoked',
  // The user has an approved device but this browser could not prove it:
  // the prompt was cancelled, WebAuthn is unsupported, or the API refused the
  // assertion. The gate shows the enrol screen in its "verify" variant.
  ASSERT_FAILED: 'assert-failed'
})

// ---------------------------------------------------------------------------
// Storage
// ---------------------------------------------------------------------------

// When localStorage refuses the write (quota, private mode, blocked
// accessor) the session still has to reach the request interceptor, so it is
// kept here for the life of the page load as well. Storage wins when it has
// a value, because it survives a reload and this does not.
let memorySession = null

const readStorage = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed.token === 'string' ? parsed : null
  } catch (error) {
    return null
  }
}

/** { token, expiresAt, deviceId } or null. */
export const getDeviceSession = () => readStorage() || memorySession

/**
 * Per-browser hint that a passkey for this account has been used or created
 * HERE. Without it, auto-asserting on a fresh browser makes the OS reach for
 * a passkey it does not hold and the user lands on a "scan this QR code"
 * sheet. Kept separate from the session so a 401 cache wipe does not turn
 * every next visit into the choice screen.
 */
export const HINT_KEY = 'fms.deviceHint'
let memoryHint = null
export const getDeviceHint = () => {
  try {
    const raw = localStorage.getItem(HINT_KEY)
    if (raw) return JSON.parse(raw)
  } catch (error) {
    /* storage blocked */
  }
  return memoryHint
}
export const setDeviceHint = deviceId => {
  if (!deviceId) return
  const hint = { deviceId: String(deviceId) }
  memoryHint = hint
  try {
    localStorage.setItem(HINT_KEY, JSON.stringify(hint))
  } catch (error) {
    /* storage blocked */
  }
}

export const setDeviceSession = ({ deviceSession, expiresAt, deviceId }) => {
  const session = { token: deviceSession, expiresAt: expiresAt || null, deviceId: deviceId || null }
  memorySession = session
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session))
  } catch (error) {
    // Storage unavailable: memorySession carries it until the next reload.
  }
}

export const clearDeviceSession = () => {
  memorySession = null
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch (error) {
    // nothing to clear
  }
}

// A minute of skew so a token that is about to expire is re-asserted here
// rather than rejected by the API mid-request.
const EXPIRY_SKEW_MS = 60 * 1000

// A session without a readable expiry is not trusted: the API always sends
// one, so its absence means the stored value is not something we wrote.
export const isDeviceSessionValid = session => {
  if (!session || !session.token || !session.expiresAt) return false
  const expiresAt = Date.parse(session.expiresAt)
  if (Number.isNaN(expiresAt)) return false
  return expiresAt - EXPIRY_SKEW_MS > Date.now()
}

/** The token to send as X-FMS-Device, or null when there is none worth sending. */
export const getDeviceSessionToken = () => {
  const session = getDeviceSession()
  return isDeviceSessionValid(session) ? session.token : null
}

// ---------------------------------------------------------------------------
// Gate code channel: interceptor → DeviceGate
// ---------------------------------------------------------------------------

const gateListeners = new Set()

/** Subscribe to gate codes published by the response interceptor. Returns an unsubscribe. */
export const subscribeGateCode = listener => {
  gateListeners.add(listener)
  return () => gateListeners.delete(listener)
}

export const publishGateCode = code => {
  gateListeners.forEach(listener => {
    try {
      listener(code)
    } catch (error) {
      console.error('device gate listener failed:', error)
    }
  })
}

// ---------------------------------------------------------------------------
// Device name default: "Chrome on macOS" from the user agent.
// ---------------------------------------------------------------------------

export const guessDeviceName = (ua = typeof navigator !== 'undefined' ? navigator.userAgent : '') => {
  const s = String(ua || '')
  let browser = 'Browser'
  if (/Edg\//.test(s)) browser = 'Edge'
  else if (/OPR\//.test(s)) browser = 'Opera'
  else if (/Firefox\//.test(s)) browser = 'Firefox'
  else if (/Chrome\//.test(s)) browser = 'Chrome'
  else if (/Safari\//.test(s)) browser = 'Safari'

  let os = 'this device'
  if (/Windows/.test(s)) os = 'Windows'
  else if (/iPhone|iPad/.test(s)) os = 'iOS'
  else if (/Mac OS X|Macintosh/.test(s)) os = 'macOS'
  else if (/Android/.test(s)) os = 'Android'
  else if (/CrOS/.test(s)) os = 'ChromeOS'
  else if (/Linux/.test(s)) os = 'Linux'

  return `${browser} on ${os}`
}

// ---------------------------------------------------------------------------
// API calls. Every one opts out of the global error toast: a gate answer is a
// state to render, and the callers here surface any real failure themselves.
// ---------------------------------------------------------------------------

const quiet = { skipGlobalErrorHandler: true }

export const errorCode = error => error?.response?.data?.code
export const errorStatus = error => error?.response?.status

/**
 * Did registration fail because THIS authenticator already holds a credential
 * for this account?
 *
 * The authenticator itself is saying so — `excludeCredentials` matched — which
 * makes it a stronger signal than the `fms.deviceHint` in localStorage, and it
 * is the one that survives cleared site data or a fresh browser profile. The
 * API puts EVERY row in excludeCredentials, revoked and pending included, so
 * this means "known to your account", NOT "approved": the caller still has to
 * check `hasActive` before sending the user to the passkey prompt.
 */
export const isAlreadyRegisteredHere = error => {
  if (!error) return false
  const name = error.name || error.cause?.name
  return name === 'InvalidStateError'
}

/** A short, human message for a failed API call or a WebAuthn exception. */
export const describeError = error => {
  if (!error) return 'Something went wrong'
  const data = error.response?.data
  if (data) return data.description || data.message || `Request failed (${error.response.status})`
  // @simplewebauthn/browser wraps DOMExceptions into WebAuthnError with a
  // `code`; the raw DOMException name is still the clearest signal.
  const name = error.name || error.cause?.name
  switch (name) {
    case 'NotAllowedError':
      return 'The passkey prompt was cancelled or timed out.'
    case 'InvalidStateError':
      return 'This authenticator is already registered for your account.'
    case 'NotSupportedError':
      return 'This browser or device does not support passkeys.'
    case 'SecurityError':
      return 'Passkeys are not available on this origin (check WEBAUTHN_RP_ID / origin).'
    case 'AbortError':
      return 'The passkey request was interrupted.'
    default:
      return error.message || 'Something went wrong'
  }
}

/** GET /v1/device/status → { mode, devices, hasActive }. Exempt from the gate. */
export const fetchDeviceStatus = () => axios.get('/v1/device/status', { ...quiet, timeout: 15000 }).then(r => r.data)

/** GET /v1/device/mine → the caller's devices, newest first. */
export const fetchMyDevices = () => axios.get('/v1/device/mine', quiet).then(r => r.data)

export const renameMyDevice = (id, deviceName) => axios.patch(`/v1/device/mine/${id}`, { deviceName }, quiet).then(r => r.data)

export const revokeMyDevice = id => axios.delete(`/v1/device/mine/${id}`, quiet).then(r => r.data)

/**
 * Prove possession of an approved passkey and store the resulting session.
 * Resolves to the API's { deviceSession, expiresAt, deviceId }.
 * Rejects with the API error or the WebAuthn exception — callers map it.
 */
export const assertDevice = async () => {
  if (!browserSupportsWebAuthn()) {
    const error = new Error('This browser does not support passkeys.')
    error.name = 'NotSupportedError'
    throw error
  }
  const options = await axios.post('/v1/device/assert/options', {}, quiet).then(r => r.data)
  const response = await startAuthentication({ optionsJSON: options })
  const session = await axios.post('/v1/device/assert/verify', { response }, quiet).then(r => r.data)
  setDeviceSession(session)
  setDeviceHint(session?.deviceId)
  return session
}

/**
 * Enrol this browser's passkey as a new device. Resolves to the created
 * UserDevices row (status `pending`); an admin approves it from User Access.
 * Rejects with the API error or the WebAuthn exception.
 */
export const registerDevice = async deviceName => {
  if (!browserSupportsWebAuthn()) {
    const error = new Error('This browser does not support passkeys.')
    error.name = 'NotSupportedError'
    throw error
  }
  const name = String(deviceName || '').trim() || guessDeviceName()
  const options = await axios.post('/v1/device/register/options', { deviceName: name }, quiet).then(r => r.data)
  const response = await startRegistration({ optionsJSON: options })
  const created = await axios.post('/v1/device/register/verify', { deviceName: name, response }, quiet).then(r => r.data)
  setDeviceHint(created?.id)
  return created
}

const hasStatus = (devices, status) => Array.isArray(devices) && devices.some(d => d.status === status)
const firstWithStatus = (devices, status) => (Array.isArray(devices) ? devices.find(d => d.status === status) || null : null)

/** Map an assertion failure to the ensure state the gate should show. */
const stateForAssertError = (error, status) => {
  const code = errorCode(error)
  if (code === GATE_CODES.ACCESS_NOT_GRANTED) return { state: ENSURE_STATE.NOT_GRANTED, status }
  if (code === GATE_CODES.DEVICE_PENDING) {
    return { state: ENSURE_STATE.PENDING, status, device: firstWithStatus(status?.devices, 'pending') }
  }
  if (code === GATE_CODES.DEVICE_REVOKED) return { state: ENSURE_STATE.REVOKED, status }
  if (code === NO_ACTIVE_DEVICE) return { state: ENSURE_STATE.ENROL, status }
  return { state: ENSURE_STATE.ASSERT_FAILED, status, error: describeError(error) }
}

/**
 * The gate's one entry point.
 *
 *   status → mode off? ready
 *          → not granted? not-granted
 *          → no approved device? pending / enrol (report mode: ready + banner)
 *          → valid stored session? ready
 *          → assert (options → passkey → verify) → ready | assert-failed
 *
 * Never throws for a gate answer. A status call that fails for any other
 * reason (network, 500) resolves READY with `unknown: true`: the UI must not
 * block the app on its own guess — the API is the real gate, and its 403 will
 * reach the interceptor and re-enter here.
 *
 * @param {object} [opts]
 * @param {boolean} [opts.force] Ignore any stored session and assert again
 *   (used after the API answered DEVICE_REQUIRED to a header we did send).
 */
export const ensureDeviceSession = async ({ force = false } = {}) => {
  let status
  try {
    status = await fetchDeviceStatus()
  } catch (error) {
    if (errorCode(error) === GATE_CODES.ACCESS_NOT_GRANTED) {
      return { state: ENSURE_STATE.NOT_GRANTED, status: null }
    }
    return { state: ENSURE_STATE.READY, status: null, mode: null, unknown: true, error: describeError(error) }
  }

  const mode = status?.mode || 'off'
  const devices = status?.devices || []
  const activeIds = devices.filter(d => d.status === 'active').map(d => String(d.id).toLowerCase())

  // Does THIS BROWSER hold a passkey for one of the account's approved
  // devices? `hasActive` is an ACCOUNT fact — somewhere, some device is
  // approved — and answering the browser's question with it is what produced
  // a screen offering both "Use passkey" and "Register this device" and
  // leaving the user to guess which applied (Yuriy, 2026-09-07). The hint is
  // written only when a passkey was created or used HERE, so it is the
  // per-browser answer.
  const hint = getDeviceHint()
  const deviceKnown = !!hint?.deviceId && activeIds.includes(String(hint.deviceId).toLowerCase())

  const base = { status, mode, hasActive: !!status?.hasActive, deviceKnown }

  if (mode === 'off') return { ...base, state: ENSURE_STATE.READY }

  if (!status?.hasActive) {
    const pending = firstWithStatus(devices, 'pending')
    // Something that has been through the queue and lost; a fresh enrolment
    // lands pending again.
    const wasRevoked = !pending && hasStatus(devices, 'revoked')
    const blocked = pending
      ? { ...base, state: ENSURE_STATE.PENDING, device: pending }
      : { ...base, state: ENSURE_STATE.ENROL, wasRevoked }
    // Report mode never blocks: the page renders and the banner nags.
    if (mode === 'report') return { ...blocked, state: ENSURE_STATE.READY, wouldBe: blocked.state, device: pending }
    return blocked
  }

  // A stored session naming a device that is no longer active (revoked or
  // rejected since the last visit) would only earn a DEVICE_REVOKED on the
  // first request; drop it here and prove possession of an active one.
  const stored = getDeviceSession()
  const storedIsActive = !stored?.deviceId || activeIds.includes(String(stored.deviceId).toLowerCase())
  if (force || (stored && !storedIsActive)) clearDeviceSession()
  if (!force && storedIsActive && isDeviceSessionValid(stored)) {
    return { ...base, state: ENSURE_STATE.READY }
  }

  // Never auto-launch the passkey prompt on a browser that has not used or
  // created a passkey for this account: if the approved passkey lives in
  // another ecosystem (say Google Password Manager on a Mac, opened now on an
  // iPhone) the OS can only offer the cross-device QR flow. Ask first.
  if (!force && !deviceKnown) {
    if (mode === 'report') return { ...base, state: ENSURE_STATE.READY, wouldBe: ENSURE_STATE.CHOOSE }
    return { ...base, state: ENSURE_STATE.CHOOSE }
  }

  try {
    await assertDevice()
    return { ...base, state: ENSURE_STATE.READY }
  } catch (error) {
    const mapped = stateForAssertError(error, status)
    if (mode === 'report') {
      return { ...base, state: ENSURE_STATE.READY, wouldBe: mapped.state, error: mapped.error }
    }
    return { ...base, ...mapped }
  }
}
